import { Dispatch, lazy, SetStateAction, Suspense, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { useTranslation } from "react-i18next"
import { Plus } from "@phosphor-icons/react"
import { Button, ButtonVariant } from "@/components/ui/button"
import { toast } from "@/hooks/useToast"
import { TelephonyInformation } from "@/modules/inbound-wizard"
import { UpdateWizardPatientFileLockProvider } from "@/modules/inbound-wizard/contexts/update-wizard-patient-file-lock-context"
import { useOpenDePatientInboundCallV2 } from "@/modules/patients/hooks/useOpenDePatientInboundCallV2"
import { SofiaEnvironment } from "@/sofia-environment"
import { default as useCallContextStore } from "@/stores/useCallContextStore"
import { Country } from "@/types/__generated__/union-types"

// The rebuilt German inbound call is unfinished, so it is lazily imported: environments with end
// users never request the chunk because the patient list hides both entry points via
// isDeInboundCallAvailableV2.
const DePatientFormV2 = lazy(() =>
  import("@/modules/inbound-wizard/components/de-v2/create-de-patient-form-v2").then((m) => ({
    default: m.DePatientFormV2,
  })),
)
const DeUpdatePatientFormWrapperV2 = lazy(() =>
  import("@/modules/inbound-wizard/components/de-v2/update-de-patient-form-v2").then((m) => ({
    default: m.DeUpdatePatientFormWrapperV2,
  })),
)

export const isDeInboundCallAvailableV2 = (country: Country) =>
  !SofiaEnvironment.AreEndUsersOnEnvironment && country === "DE"

interface CreateDePatientWizardV2Props {
  variant?: ButtonVariant
  defaultCountry: Country
  patientQuery?: string
  setPatientQuery?: (value: string) => void
}
export const CreateDePatientWizardV2 = (props: CreateDePatientWizardV2Props) => {
  const { variant, defaultCountry, patientQuery, setPatientQuery } = props

  const callerPhoneNumber = useCallContextStore((state) => state.callerPhoneNumber)
  const callLineCode = useCallContextStore((state) => state.callLineCode)
  const callId = useCallContextStore((state) => state.callID)
  const inboundCallStartedAt = useCallContextStore((state) => state.inboundCallStartedAt)
  const callLineLanguage = useCallContextStore((state) => state.callLineLanguage)

  return (
    <CreateDePatientWizardInternalV2
      variant={variant}
      defaultCountry={defaultCountry}
      patientQuery={patientQuery}
      setPatientQuery={setPatientQuery}
      telephonyInformation={{
        callerPhoneNumber,
        callLineCode,
        callId,
        getNotes: () => useCallContextStore.getState().temporaryNotes,
        inboundCallStartedAt,
        callLineLanguage,
      }}
    />
  )
}

interface CreateDePatientWizardInternalV2Props {
  variant?: ButtonVariant
  defaultCountry: Country
  telephonyInformation: TelephonyInformation
  patientQuery?: string
  setPatientQuery?: (value: string) => void
}
const CreateDePatientWizardInternalV2 = (props: CreateDePatientWizardInternalV2Props) => {
  const { variant, defaultCountry, telephonyInformation, patientQuery, setPatientQuery } = props
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const { openDePatientInboundCallV2, dePatientInboundCallDialogV2 } = useOpenDePatientInboundCallV2()

  const handleOpenChange = (open: boolean) => setOpen(open)

  return (
    <>
      <Button variant={variant} onClick={() => setOpen(true)} data-testid="open-create-de-ibc-button-v2">
        <Plus />
        {t("patient.header.patientDeInboundCallV2")}
      </Button>
      <Suspense>
        {open ? (
          <DePatientFormV2
            onOpenChange={handleOpenChange}
            patientQuery={patientQuery}
            setPatientQuery={setPatientQuery}
            telephonyInformation={telephonyInformation}
            defaultCountry={defaultCountry}
            openPatientInboundCall={openDePatientInboundCallV2}
          />
        ) : null}
      </Suspense>
      {dePatientInboundCallDialogV2}
    </>
  )
}
interface UpdateDePatientWizardV2Props {
  patientId: string
  patientName: string
  open: boolean
  onOpenChange: (open: boolean) => void
  shouldHideCallReason?: boolean
  setPatientQuery?: Dispatch<SetStateAction<string>>
}
export const UpdateDePatientWizardV2 = (props: UpdateDePatientWizardV2Props) => {
  const { patientId, patientName, open, onOpenChange, shouldHideCallReason, setPatientQuery } = props

  const callerPhoneNumber = useCallContextStore((state) => state.callerPhoneNumber)
  const callId = useCallContextStore((state) => state.callID)
  const callLineCode = useCallContextStore((state) => state.callLineCode)
  const inboundCallStartedAt = useCallContextStore((state) => state.inboundCallStartedAt)
  const callLineLanguage = useCallContextStore((state) => state.callLineLanguage)

  return (
    <UpdateDePatientWizardInternalV2
      patientId={patientId}
      patientName={patientName}
      open={open}
      onOpenChange={onOpenChange}
      setPatientQuery={setPatientQuery}
      shouldHideCallReason={!!shouldHideCallReason}
      telephonyInformation={{
        callerPhoneNumber,
        callLineCode,
        callId,
        getNotes: () => useCallContextStore.getState().temporaryNotes,
        inboundCallStartedAt,
        callLineLanguage,
      }}
    />
  )
}

interface UpdateDePatientFormPropsV2 {
  patientId: string
  telephonyInformation: TelephonyInformation
  setPatientQuery?: Dispatch<SetStateAction<string>>
  shouldHideCallReason: boolean
}
const UpdateDePatientFormV2 = (props: UpdateDePatientFormPropsV2) => <DeUpdatePatientFormWrapperV2 {...props} />

interface UpdateDePatientWizardInternalV2Props {
  patientId: string
  patientName: string
  open: boolean
  onOpenChange: (open: boolean) => void
  setPatientQuery?: Dispatch<SetStateAction<string>>
  telephonyInformation: TelephonyInformation
  shouldHideCallReason: boolean
}
const UpdateDePatientWizardInternalV2 = (props: UpdateDePatientWizardInternalV2Props) => {
  const { patientId, patientName, open, telephonyInformation, onOpenChange, setPatientQuery, shouldHideCallReason } =
    props
  const { t } = useTranslation()

  const ErrorFallback = () => {
    toast({ description: t("patient.toast.error") })
    return null
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      {open && (
        <Suspense>
          <UpdateWizardPatientFileLockProvider
            patientId={patientId}
            patientName={patientName}
            shouldHideCallReason={shouldHideCallReason}
            onOpenChange={onOpenChange}>
            <UpdateDePatientFormV2
              patientId={patientId}
              telephonyInformation={telephonyInformation}
              setPatientQuery={setPatientQuery}
              shouldHideCallReason={shouldHideCallReason}
            />
          </UpdateWizardPatientFileLockProvider>
        </Suspense>
      )}
    </ErrorBoundary>
  )
}
